// Frontend gateway to the signing Cloud Functions.
// All signing-relevant writes go through here — never write to Firestore directly.

import { httpsCallable } from 'firebase/functions';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import {
  startRegistration,
  startAuthentication,
  browserSupportsWebAuthn,
  platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';
import { db, functions } from '../../../firebase';
import { TERMS_VERSION } from '../terms';

const setupAssentFn = httpsCallable(functions, 'setupSignatureAssent');
const setupTypedNameFn = httpsCallable(functions, 'setupSignatureTypedName');
const setupPinFn = httpsCallable(functions, 'setupSignaturePin');
const setupWebAuthnBeginFn = httpsCallable(functions, 'setupSignatureWebAuthnBegin');
const setupWebAuthnFinishFn = httpsCallable(functions, 'setupSignatureWebAuthnFinish');
const signDocumentBeginFn = httpsCallable(functions, 'signDocumentBegin');
const signDocumentFn = httpsCallable(functions, 'signDocument');

const deviceLabel = () => {
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/.test(ua)) return 'iOS device';
  if (/Macintosh/.test(ua)) return 'Mac';
  if (/Android/.test(ua)) return 'Android device';
  if (/Windows/.test(ua)) return 'Windows';
  return 'Web browser';
};

// ── Setup ───────────────────────────────────────────────────────────────────

export const setupAssentSignature = async ({ legalName } = {}) => {
  const result = await setupAssentFn({ legalName, termsVersion: TERMS_VERSION });
  return result.data;
};

export const setupTypedNameSignature = async ({ legalName }) => {
  const result = await setupTypedNameFn({ legalName, termsVersion: TERMS_VERSION });
  return result.data;
};

export const setupPinSignature = async ({ legalName, pin }) => {
  const result = await setupPinFn({ legalName, pin, termsVersion: TERMS_VERSION });
  return result.data;
};

export const setupBiometricSignature = async ({ legalName }) => {
  if (!browserSupportsWebAuthn()) {
    throw new Error('Your browser does not support biometric signing.');
  }
  const platformAvailable = await platformAuthenticatorIsAvailable();
  if (!platformAvailable) {
    throw new Error('No fingerprint or face recognition was found on this device.');
  }

  const beginResult = await setupWebAuthnBeginFn({
    legalName,
    termsVersion: TERMS_VERSION,
  });
  const { options } = beginResult.data;

  const response = await startRegistration({ optionsJSON: options });

  const finishResult = await setupWebAuthnFinishFn({
    response,
    deviceLabel: deviceLabel(),
  });
  return finishResult.data;
};

// ── Read setup state ────────────────────────────────────────────────────────

export const getSignatureSetup = async (uid) => {
  const snap = await getDoc(doc(db, 'users', uid));
  return snap.exists() ? snap.data().signatureSetup || null : null;
};

export const subscribeToSignatureSetup = (uid, callback) => {
  return onSnapshot(doc(db, 'users', uid), (snap) => {
    callback(snap.exists() ? snap.data().signatureSetup || null : null);
  });
};

export const subscribeToDocumentSignature = (documentId, uid, callback) => {
  return onSnapshot(
    doc(db, 'signableDocuments', documentId, 'signatures', uid),
    (snap) => {
      callback(snap.exists() ? { id: snap.id, ...snap.data() } : null);
    }
  );
};

// ── Signing ─────────────────────────────────────────────────────────────────

export const signWithAssent = async ({ documentId }) => {
  const result = await signDocumentFn({
    documentId,
    method: 'assent',
    payload: { agreed: true },
  });
  return result.data;
};

export const signWithTypedName = async ({ documentId, typedName }) => {
  const result = await signDocumentFn({
    documentId,
    method: 'typed_name',
    payload: { typedName },
  });
  return result.data;
};

export const signWithPin = async ({ documentId, pin }) => {
  const result = await signDocumentFn({
    documentId,
    method: 'pin',
    payload: { pin },
  });
  return result.data;
};

export const signWithBiometric = async ({ documentId }) => {
  if (!browserSupportsWebAuthn()) {
    throw new Error('Your browser does not support biometric signing.');
  }

  const beginResult = await signDocumentBeginFn({ documentId });
  const { options } = beginResult.data;

  const response = await startAuthentication({ optionsJSON: options });

  const result = await signDocumentFn({
    documentId,
    method: 'biometric',
    payload: { response },
  });
  return result.data;
};

// ── Capability checks ───────────────────────────────────────────────────────

export const canUseBiometric = async () => {
  if (!browserSupportsWebAuthn()) return false;
  try {
    return await platformAuthenticatorIsAvailable();
  } catch {
    return false;
  }
};
